import { useState, useMemo } from "react";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Card,
  IconButton,
  Autocomplete,
  TextField,
  LinearProgress,
  Alert,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ContentCopy,
  Publish,
  Search,
  Add,
  LocalFlorist,
  Cake,
  Restaurant,
  Delete as DeleteIcon,
  Star,
  Diamond,
} from "@mui/icons-material";
import { format, startOfWeek, addWeeks, addDays, subWeeks } from "date-fns";
import { getBusinessTodayDate } from "@/utils/businessTime";
import { fr } from "date-fns/locale";
import { useToastStore } from "@/store/toastStore";
import { Button, PageHeader, LoadingSpinner, ErrorMessage, FiltersCard } from "@/components/ui";
import {
  weeklyMenuService,
  type MenuSlot,
  type MenuSlotType,
  type UpsertSlotInput,
} from "@/services/weeklyMenu.service";
import { productService, type Product, type DishCategory } from "@/services/product.service";
import { estimationService } from "@/services/estimation.service";

/** Prépare le corps d'upsert à partir d'un slot existant (évite d'écraser portions / override / notes lors d'un simple décalage d'ordre). */
function menuSlotToUpsertInput(
  date: string,
  slotType: MenuSlotType,
  slot: MenuSlot,
  displayOrder: number
): UpsertSlotInput {
  const priceOverride =
    slot.priceOverride != null && slot.priceOverride !== ""
      ? typeof slot.priceOverride === "number"
        ? slot.priceOverride
        : Number(slot.priceOverride)
      : null;
  return {
    date,
    slotType,
    displayOrder,
    productId: slot.productId ?? null,
    priceOverride,
    estimatedPortions: slot.estimatedPortions ?? null,
    isSwitch: slot.isSwitch ?? false,
    notes: slot.notes ?? null,
  };
}

const SLOT_LABELS: Record<MenuSlotType, { label: string; color?: string }> = {
  ENTREE: { label: "Entrée du Jour" },
  PLATS: { label: "Les Plats" },
  LEGUMES_100: { label: "100 % Légumes", color: "success.main" },
  DESSERT: { label: "Dessert du Jour" },
  PREMIUM: { label: "Option Premium", color: "warning.main" },
  PREMIUM_PLUS: { label: "Dessert Premium", color: "warning.main" },
};

const SLOT_TO_PRODUCT_TYPE: Record<MenuSlotType, "STARTER" | "MAIN_COURSE" | "SIDE" | "DESSERT"> = {
  ENTREE: "STARTER",
  PLATS: "MAIN_COURSE",
  LEGUMES_100: "SIDE",
  DESSERT: "DESSERT",
  PREMIUM: "MAIN_COURSE",
  PREMIUM_PLUS: "DESSERT",
};

const DISH_CATEGORY_LABELS: Record<DishCategory, string> = {
  FISH: "Poisson",
  GRILLED_MEAT: "Grillade",
  VEGETARIAN: "Végétarien",
  TRADITIONAL: "Traditionnel",
  EXOTIC: "Exotique",
  STEW: "Ragoûts",
  OTHER: "Autre",
};

const WEEKDAYS = [0, 1, 2, 3, 4] as const; // Lun–Ven

/** Normalise une date (ISO ou YYYY-MM-DD) en YYYY-MM-DD pour comparaison */
function toDateKey(val: string): string {
  return typeof val === "string" ? val.slice(0, 10) : "";
}

/** Semaine métier : toujours lundi–vendredi, quel que soit le fuseau. */
function getMondayOfWeek(d: Date): Date {
  return startOfWeek(d, { weekStartsOn: 1 });
}

/**
 * Parse une chaîne YYYY-MM-DD comme date locale (midi) pour éviter tout décalage
 * en fuseaux négatifs (ex. Guyane). Garantit que la semaine affichée reste Lun–Ven.
 */
function parseLocalDateOnly(dateStr: string): Date {
  const s = typeof dateStr === "string" ? dateStr.slice(0, 10) : "";
  const [y, m, d] = s.split("-").map(Number);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return new Date(NaN);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function toWeekdayCodeFromDate(dateStr: string): string | null {
  const date = parseLocalDateOnly(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.getDay();
  const map = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
  return map[day] ?? null;
}

function isProductEligibleForDate(product: Product, dateStr: string): boolean {
  if (!product.isRecurrent) return true;
  const recurrentDays = product.recurrentDays ?? [];
  if (recurrentDays.length === 0) return true;
  const weekday = toWeekdayCodeFromDate(dateStr);
  if (!weekday) return true;
  return recurrentDays.includes(weekday as typeof recurrentDays[number]);
}

export function MenusPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();

  const [weekAnchor, setWeekAnchor] = useState<Date>(() => getMondayOfWeek(getBusinessTodayDate()));
  const weekStartDate = format(getMondayOfWeek(weekAnchor), "yyyy-MM-dd");
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateTargetWeek, setDuplicateTargetWeek] = useState<string>("");

  // Historique : liste de semaines L–V (12 passées, courante, 4 à venir) pour navigation rapide
  const weekOptions = useMemo(() => {
    const today = getMondayOfWeek(getBusinessTodayDate());
    const options: { value: string; label: string }[] = [];
    for (let i = 12; i >= 1; i--) {
      const mon = subWeeks(today, i);
      options.push({ value: format(mon, "yyyy-MM-dd"), label: `Lun ${format(mon, "d MMM")} – Ven ${format(addDays(mon, 4), "d MMM yyyy", { locale: fr })}` });
    }
    options.push({ value: format(today, "yyyy-MM-dd"), label: `Cette semaine : Lun ${format(today, "d MMM")} – Ven ${format(addDays(today, 4), "d MMM yyyy", { locale: fr })}` });
    for (let i = 1; i <= 4; i++) {
      const mon = addWeeks(today, i);
      options.push({ value: format(mon, "yyyy-MM-dd"), label: `Lun ${format(mon, "d MMM")} – Ven ${format(addDays(mon, 4), "d MMM yyyy", { locale: fr })}` });
    }
    return options;
  }, []);

  const { data: menu, isLoading, error } = useQuery({
    queryKey: ["weekly-menu", weekStartDate],
    queryFn: () => weeklyMenuService.getByWeek(weekStartDate),
  });

  const { data: products = [] } = useQuery({
    queryKey: ["products", "available"],
    queryFn: () => productService.getAll({ availability: "available" }),
  });

  // Quantités commandées par jour (une requête par jour pour afficher prévu vs commandé par jour)
  const dayDatesForEstimations = useMemo(
    () => WEEKDAYS.map((i) => format(addDays(parseLocalDateOnly(weekStartDate), i), "yyyy-MM-dd")),
    [weekStartDate]
  );
  const estimationQueries = useQueries({
    queries: dayDatesForEstimations.map((dateStr) => ({
      queryKey: ["estimations", "day", dateStr],
      queryFn: () => estimationService.getEstimations(dateStr, dateStr),
      enabled: !!menu?.id,
    })),
  });
  const orderedQuantitiesByDay = useMemo(() => {
    const map = new Map<string, number>();
    dayDatesForEstimations.forEach((dateStr, i) => {
      const data = estimationQueries[i]?.data;
      const total = data?.byType?.reduce((sum, t) => sum + t.count, 0) ?? 0;
      map.set(dateStr, total);
    });
    return map;
  }, [dayDatesForEstimations, estimationQueries]);

  const duplicateToWeekMutation = useMutation({
    mutationFn: ({ menuId, targetWeekStartDate }: { menuId: string; targetWeekStartDate: string }) =>
      weeklyMenuService.duplicateToWeek(menuId, targetWeekStartDate),
    onSuccess: (newMenu) => {
      queryClient.invalidateQueries({ queryKey: ["weekly-menu"] });
      success("Semaine dupliquée vers la semaine sélectionnée");
      setWeekAnchor(getMondayOfWeek(parseLocalDateOnly(newMenu.weekStartDate)));
      setDuplicateDialogOpen(false);
      setDuplicateTargetWeek("");
    },
    onError: (err: unknown) => {
      showError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "Erreur lors de la duplication");
    },
  });

  const handleOpenDuplicateDialog = () => {
    setDuplicateTargetWeek("");
    setDuplicateDialogOpen(true);
  };

  const handleConfirmDuplicate = () => {
    if (!menu?.id || !duplicateTargetWeek) return;
    duplicateToWeekMutation.mutate({ menuId: menu.id, targetWeekStartDate: duplicateTargetWeek });
  };

  const duplicateTargetWeekOptions = useMemo(
    () => weekOptions.filter((opt) => opt.value !== weekStartDate),
    [weekOptions, weekStartDate]
  );

  // Menu de la semaine cible (pour alerter si brouillon existant ou désactiver si publié)
  const { data: targetWeekMenu, isLoading: targetWeekMenuLoading } = useQuery({
    queryKey: ["weekly-menu-exists", duplicateTargetWeek],
    queryFn: () => weeklyMenuService.getByWeekIfExists(duplicateTargetWeek),
    enabled: duplicateDialogOpen && !!duplicateTargetWeek,
  });

  const targetWeekIsPublished = targetWeekMenu?.status === "PUBLISHED";
  const targetWeekIsDraft = targetWeekMenu != null && targetWeekMenu.status !== "PUBLISHED";

  const publishMutation = useMutation({
    mutationFn: (id: string) => weeklyMenuService.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["weekly-menu"] });
      success("Menu publié");
    },
    onError: (err: unknown) => {
      showError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "Erreur lors de la publication");
    },
  });

  const upsertSlotMutation = useMutation({
    mutationFn: ({
      menuId,
      slotId,
      input,
    }: {
      menuId: string;
      slotId?: string;
      input: Parameters<typeof weeklyMenuService.upsertSlot>[1];
    }) => weeklyMenuService.upsertSlot(menuId, input, slotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["weekly-menu"] });
    },
    onError: (err: unknown) => {
      showError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "Erreur lors de l'enregistrement");
    },
  });

  const deleteSlotMutation = useMutation({
    mutationFn: ({ menuId, slotId }: { menuId: string; slotId: string }) =>
      weeklyMenuService.deleteSlot(menuId, slotId),
    onMutate: async ({ slotId }) => {
      await queryClient.cancelQueries({ queryKey: ["weekly-menu", weekStartDate] });
      const previous = queryClient.getQueryData<{ slots: MenuSlot[] }>(["weekly-menu", weekStartDate]);
      queryClient.setQueryData<{ slots: MenuSlot[] }>(["weekly-menu", weekStartDate], (old) => {
        if (!old?.slots) return old;
        return { ...old, slots: old.slots.filter((s) => s.id !== slotId) };
      });
      return { previous };
    },
    onSuccess: () => {
      success("Créneau supprimé");
    },
    onError: (err: unknown, _variables, context) => {
      if (context?.previous != null) {
        queryClient.setQueryData(["weekly-menu", weekStartDate], context.previous);
      }
      showError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "Erreur lors de la suppression");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["weekly-menu"] });
    },
  });

  const productsByType = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const slotType of ["ENTREE", "PLATS", "LEGUMES_100", "DESSERT", "PREMIUM", "PREMIUM_PLUS"] as const) {
      const type = SLOT_TO_PRODUCT_TYPE[slotType];
      let filtered = products.filter((p) => p.type === type);
      
      // Pour PREMIUM, ne montrer que les plats Premium (isPremium = true)
      if (slotType === "PREMIUM") {
        filtered = filtered.filter((p) => p.isPremium === true);
      }
      // Pour PREMIUM_PLUS, ne montrer que les desserts Premium (isPremium = true)
      if (slotType === "PREMIUM_PLUS") {
        filtered = filtered.filter((p) => p.isPremium === true);
      }
      
      map.set(slotType, filtered);
    }
    return map;
  }, [products]);

  const slotsByDayAndType = useMemo(() => {
    if (!menu) return new Map<string, Map<MenuSlotType, MenuSlot[]>>();
    const map = new Map<string, Map<MenuSlotType, MenuSlot[]>>();
    for (const i of WEEKDAYS) {
      const d = format(addDays(parseLocalDateOnly(menu.weekStartDate), i), "yyyy-MM-dd");
      const byType = new Map<MenuSlotType, MenuSlot[]>();
      for (const slot of menu.slots.filter((s) => toDateKey(s.date) === d)) {
        const list = byType.get(slot.slotType) ?? [];
        list.push(slot);
        byType.set(slot.slotType, list.sort((a, b) => a.displayOrder - b.displayOrder));
      }
      map.set(d, byType);
    }
    return map;
  }, [menu]);

  const totalPortionsByDay = useMemo(() => {
    const map = new Map<string, number>();
    if (!menu) return map;
    for (const slot of menu.slots) {
      const d = toDateKey(slot.date);
      const n = slot.estimatedPortions ?? 0;
      map.set(d, (map.get(d) ?? 0) + n);
    }
    return map;
  }, [menu]);

  /** Pour chaque jour L–V : complet = au moins 1 entrée, 1 plat, 1 légumes, 1 dessert. Détail des manques pour guider l’utilisateur. */
  const REQUIRED_SLOT_TYPES: { type: MenuSlotType; label: string }[] = [
    { type: "ENTREE", label: "Entrée" },
    { type: "PLATS", label: "Plats" },
    { type: "LEGUMES_100", label: "100 % lég." },
    { type: "DESSERT", label: "Dessert" },
  ];
  const { daysComplete, daySummaries } = useMemo(() => {
    const dayDates = menu
      ? WEEKDAYS.map((i) => format(addDays(parseLocalDateOnly(menu.weekStartDate), i), "yyyy-MM-dd"))
      : [];
    let daysComplete = 0;
    const daySummaries: { date: string; dayLabel: string; missing: string[]; isComplete: boolean }[] = [];
    for (const d of dayDates) {
      const byType = slotsByDayAndType.get(d);
      const missing: string[] = [];
      for (const { type, label } of REQUIRED_SLOT_TYPES) {
        if (!byType?.get(type)?.some((s) => s.productId)) missing.push(label);
      }
      const isComplete = missing.length === 0;
      if (isComplete) daysComplete += 1;
      const dayLabel = format(parseLocalDateOnly(d), "EEE d MMM", { locale: fr });
      daySummaries.push({ date: d, dayLabel, missing, isComplete });
    }
    return { daysComplete, daySummaries };
  }, [menu, slotsByDayAndType]);

  /** Alertes selon les règles métier (spec 9.7.4) */
  const menuAlerts = useMemo(() => {
    const alerts: { severity: "warning" | "info"; message: string }[] = [];
    if (!menu?.slots?.length) return alerts;

    const slotsWithProduct = menu.slots.filter((s) => s.productId && s.product);
    const dayDates = WEEKDAYS.map((i) => format(addDays(parseLocalDateOnly(menu.weekStartDate), i), "yyyy-MM-dd"));

    // Même produit sur 2 jours consécutifs
    const productIdsByDay = new Map<string, Set<string>>();
    for (const d of dayDates) productIdsByDay.set(d, new Set());
    for (const slot of slotsWithProduct) {
      const d = toDateKey(slot.date);
      if (slot.productId) productIdsByDay.get(d)?.add(slot.productId);
    }
    const productNames = new Map<string, string>();
    for (const slot of slotsWithProduct) {
      if (slot.product?.name && slot.productId) productNames.set(slot.productId, slot.product.name);
    }
    const consecutiveDuplicateIds = new Set<string>();
    for (let i = 0; i < dayDates.length - 1; i++) {
      const a = productIdsByDay.get(dayDates[i]) ?? new Set();
      const b = productIdsByDay.get(dayDates[i + 1]) ?? new Set();
      for (const id of a) {
        if (b.has(id)) consecutiveDuplicateIds.add(id);
      }
    }
    for (const id of consecutiveDuplicateIds) {
      alerts.push({
        severity: "warning",
        message: `Même produit sur 2 jours consécutifs : ${productNames.get(id) ?? id}`,
      });
    }

    // Même légume « 100 % légumes » répété dans la semaine
    const legumes100ByProduct = new Map<string, number>();
    for (const slot of menu.slots) {
      if (slot.slotType !== "LEGUMES_100" || !slot.productId) continue;
      legumes100ByProduct.set(slot.productId, (legumes100ByProduct.get(slot.productId) ?? 0) + 1);
    }
    for (const [pid, count] of legumes100ByProduct) {
      if (count > 1) {
        const name = productNames.get(pid) ?? menu.slots.find((s) => s.productId === pid)?.product?.name ?? pid;
        alerts.push({
          severity: "warning",
          message: `Même « 100 % légumes » répété dans la semaine : ${name}`,
        });
      }
    }

    // Aucun plat végétarien
    const hasVegetarian = slotsWithProduct.some((s) => s.product?.isVegetarian === true);
    if (!hasVegetarian && slotsWithProduct.length > 0) {
      alerts.push({ severity: "info", message: "Aucun plat végétarien dans la semaine." });
    }

    // Aucun créneau avec option Switch cochée
    const hasSwitch = slotsWithProduct.some((s) => s.isSwitch === true);
    if (!hasSwitch && slotsWithProduct.length > 0) {
      alerts.push({ severity: "info", message: "Aucun plat avec option Switch cochée dans la semaine." });
    }

    return alerts;
  }, [menu]);

  const handlePrevWeek = () => setWeekAnchor((d) => addWeeks(d, -1));
  const handleNextWeek = () => setWeekAnchor((d) => addWeeks(d, 1));

  const handleUpsertSlot = (
    menuId: string,
    date: string,
    slotType: MenuSlotType,
    displayOrder: number,
    slotId?: string,
    productId?: string | null,
    estimatedPortions?: number | null,
    isSwitch?: boolean
  ) => {
    upsertSlotMutation.mutate({
      menuId,
      slotId,
      input: {
        date,
        slotType,
        displayOrder,
        productId: productId ?? null,
        estimatedPortions: estimatedPortions ?? null,
        isSwitch,
      },
    });
  };

  const handleAddPlat = async (menuId: string, date: string) => {
    const existing = [...(slotsByDayAndType.get(date)?.get("PLATS") ?? [])].sort(
      (a, b) => b.displayOrder - a.displayOrder
    );
    try {
      for (const slot of existing) {
        await upsertSlotMutation.mutateAsync({
          menuId,
          slotId: slot.id,
          input: menuSlotToUpsertInput(date, "PLATS", slot, slot.displayOrder + 1),
        });
      }
      await upsertSlotMutation.mutateAsync({
        menuId,
        input: {
          date,
          slotType: "PLATS",
          displayOrder: 0,
          productId: null,
          estimatedPortions: null,
        },
      });
    } catch {
      /* onError de la mutation */
    }
  };

  const handleAddDessert = (menuId: string, date: string) => {
    const byType = slotsByDayAndType.get(date)?.get("DESSERT") ?? [];
    const nextOrder = byType.length;
    handleUpsertSlot(menuId, date, "DESSERT", nextOrder);
  };

  if (isLoading || !menu) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Menu de la semaine" subtitle="Planification L–V" />
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Menu de la semaine" subtitle="Planification L–V" />
        <ErrorMessage message={(error as Error).message} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>
      <PageHeader
        title="Menu de la semaine"
        subtitle="Planification L–V : entrées, plats, 100 % légumes, desserts"
        action={
          <Button
            variant="primary"
            size="small"
            startIcon={<Publish />}
            onClick={() => publishMutation.mutate(menu.id)}
            disabled={menu.status === "PUBLISHED" || publishMutation.isPending}
          >
            Publier
          </Button>
        }
      />

      <FiltersCard>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={handlePrevWeek} aria-label="Semaine précédente">
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2" fontWeight={600}>
            {format(parseLocalDateOnly(menu.weekStartDate), "d MMM", { locale: fr })} –{" "}
            {format(parseLocalDateOnly(menu.weekEndDate), "d MMM yyyy", { locale: fr })}
          </Typography>
          <IconButton size="small" onClick={handleNextWeek} aria-label="Semaine suivante">
            <ChevronRight />
          </IconButton>
        </Box>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="menu-week-select-label">Historique / Semaine</InputLabel>
          <Select
            labelId="menu-week-select-label"
            value={weekStartDate}
            label="Historique / Semaine"
            onChange={(e) => setWeekAnchor(getMondayOfWeek(parseLocalDateOnly(e.target.value as string)))}
          >
            {weekOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outline"
          size="small"
          startIcon={<ContentCopy />}
          onClick={handleOpenDuplicateDialog}
          disabled={duplicateToWeekMutation.isPending}
        >
          Dupliquer la semaine
        </Button>
      </FiltersCard>

      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Dupliquer vers quelle semaine ?</DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel id="duplicate-target-week-label">Semaine cible</InputLabel>
                  <Select
                    labelId="duplicate-target-week-label"
                    value={duplicateTargetWeek}
                    label="Semaine cible"
                    onChange={(e) => setDuplicateTargetWeek(e.target.value as string)}
                  >
                    {duplicateTargetWeekOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {targetWeekMenuLoading && duplicateTargetWeek && (
                  <Box sx={{ mt: 1.5 }}>
                    <LinearProgress sx={{ borderRadius: 1 }} />
                  </Box>
                )}
                {!targetWeekMenuLoading && targetWeekIsPublished && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    Un menu publié existe déjà pour cette semaine. La duplication vers cette semaine est impossible.
                  </Alert>
                )}
                {!targetWeekMenuLoading && targetWeekIsDraft && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    Un menu brouillon existe déjà pour cette semaine. La duplication écrasera son contenu.
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  startIcon={<ContentCopy />}
                  onClick={handleConfirmDuplicate}
                  disabled={
                    !duplicateTargetWeek ||
                    duplicateToWeekMutation.isPending ||
                    targetWeekIsPublished ||
                    targetWeekMenuLoading
                  }
                >
                  Dupliquer
                </Button>
              </DialogActions>
      </Dialog>

      <Box sx={{ display: "flex", gap: 3, flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, overflowX: "auto", overflowY: "auto", pb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, minWidth: "max-content" }}>
            {WEEKDAYS.map((i) => {
              const dayDate = addDays(parseLocalDateOnly(menu.weekStartDate), i);
              const dateStr = format(dayDate, "yyyy-MM-dd");
              const dayLabel = format(dayDate, "EEEE d MMM", { locale: fr });
              const byType = slotsByDayAndType.get(dateStr);
              void (totalPortionsByDay.get(dateStr) ?? 0);

              return (
                <Card
                  key={dateStr}
                  sx={{
                    width: 320,
                    minWidth: 320,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: i === 0 ? "primary.main" : "grey.400",
                        }}
                      />
                      <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase">
                        {dayLabel}
                      </Typography>
                    </Box>
                  </Box>

                  {(["ENTREE", "PLATS", "LEGUMES_100", "DESSERT", "PREMIUM", "PREMIUM_PLUS"] as const).map((slotType) => {
                    const meta = SLOT_LABELS[slotType];
                    const slots = byType?.get(slotType) ?? [];
                    const isPlats = slotType === "PLATS";
                    const isDessert = slotType === "DESSERT";
                    const isMultiSlot = isPlats || isDessert;
                    const options = (productsByType.get(slotType) ?? []).filter((p) =>
                      isProductEligibleForDate(p, dateStr)
                    );

                    return (
                      <Card
                        key={slotType}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          borderColor: meta.color ? `${meta.color}` : "divider",
                        }}
                      >
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                              textTransform: "uppercase",
                              bgcolor: meta.color ? `${meta.color}20` : "action.selected",
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                            }}
                          >
                            {meta.label}
                          </Typography>
                        </Box>

                        {isPlats && (
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Add />}
                              onClick={() => handleAddPlat(menu.id, dateStr)}
                              disabled={upsertSlotMutation.isPending}
                              sx={{ textTransform: "none", minWidth: 0 }}
                            >
                              Ajouter un plat
                            </Button>
                          </Box>
                        )}
                        {isDessert && (
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Add />}
                              onClick={() => handleAddDessert(menu.id, dateStr)}
                              disabled={upsertSlotMutation.isPending}
                              sx={{ textTransform: "none", minWidth: 0 }}
                            >
                              Ajouter un dessert
                            </Button>
                          </Box>
                        )}

                        {!isMultiSlot && slots.length === 0 && (
                          <SlotRow
                            key={`${dateStr}-${slotType}-empty`}
                            menuId={menu.id}
                            dateStr={dateStr}
                            slotType={slotType}
                            displayOrder={0}
                            options={options}
                            slot={null}
                            onUpsert={handleUpsertSlot}
                            isPending={upsertSlotMutation.isPending}
                          />
                        )}
                        {!isMultiSlot && slots[0] && (
                          <SlotRow
                            key={slots[0].id}
                            menuId={menu.id}
                            dateStr={dateStr}
                            slotType={slotType}
                            displayOrder={0}
                            options={options}
                            slot={slots[0]}
                            onUpsert={handleUpsertSlot}
                            isPending={upsertSlotMutation.isPending}
                          />
                        )}

                        {isMultiSlot &&
                          slots.map((slot) => (
                            <SlotRow
                              key={slot.id}
                              menuId={menu.id}
                              dateStr={dateStr}
                              slotType={slotType}
                              displayOrder={slot.displayOrder}
                              options={options}
                              slot={slot}
                              onUpsert={handleUpsertSlot}
                              onDelete={() => deleteSlotMutation.mutate({ menuId: menu.id, slotId: slot.id })}
                              isPending={upsertSlotMutation.isPending}
                            />
                          ))}
                        {isPlats && slots.length === 0 && (
                          <Box
                            sx={{
                              border: "1px dashed",
                              borderColor: "divider",
                              borderRadius: 1,
                              p: 1.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Restaurant sx={{ color: "text.disabled" }} />
                            <Typography variant="body2" color="text.secondary">
                              Aucun plat
                            </Typography>
                          </Box>
                        )}
                        {isDessert && slots.length === 0 && (
                          <Box
                            sx={{
                              border: "1px dashed",
                              borderColor: "divider",
                              borderRadius: 1,
                              p: 1.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Cake sx={{ color: "text.disabled" }} />
                            <Typography variant="body2" color="text.secondary">
                              Aucun dessert
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    );
                  })}
                </Card>
              );
            })}
          </Box>
        </Box>

        <Card
          sx={{
            width: 320,
            minWidth: 320,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 2,
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={700} textTransform="uppercase">
              Cuisine & quantités
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", display: "block", mb: 1 }}>
                Jours complets (L–V)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {daysComplete}/5 jours avec entrée, plats, 100 % lég. et dessert
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {daySummaries.map((day) => (
                  <Box
                    key={day.date}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: day.isComplete ? "success.50" : "action.hover",
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {day.dayLabel}
                    </Typography>
                    {day.isComplete ? (
                      <Chip size="small" label="Complet" color="success" sx={{ fontWeight: 600 }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: "right" }}>
                        Manque : {day.missing.join(", ")}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

            {menuAlerts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", display: "block", mb: 1 }}>
                  Alertes règles métier
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {menuAlerts.map((alertItem, idx) => (
                    <Alert key={idx} severity={alertItem.severity} sx={{ py: 0.5 }}>
                      {alertItem.message}
                    </Alert>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", display: "block", mb: 1 }}>
                Volume prévu vs commandé (par jour)
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {daySummaries.map((day) => {
                  const prevu = totalPortionsByDay.get(day.date) ?? 0;
                  const commande = orderedQuantitiesByDay.get(day.date) ?? 0;
                  return (
                    <Box
                      key={day.date}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto auto",
                        gap: 1.5,
                        alignItems: "center",
                        p: 1,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {day.dayLabel}
                      </Typography>
                      <Typography variant="body2" color="primary.main" fontWeight={700}>
                        {prevu} prévues
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={700}>
                        {commande} commandées
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Statut : </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {menu.status === "DRAFT" ? "Brouillon" : menu.status === "PUBLISHED" ? "Publié" : "Archivé"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}

interface SlotRowProps {
  menuId: string;
  dateStr: string;
  slotType: MenuSlotType;
  displayOrder: number;
  options: Product[];
  slot: MenuSlot | null;
  onUpsert: (
    menuId: string,
    date: string,
    slotType: MenuSlotType,
    displayOrder: number,
    slotId?: string,
    productId?: string | null,
    estimatedPortions?: number | null,
    isSwitch?: boolean
  ) => void;
  onDelete?: () => void;
  isPending: boolean;
}

function SlotRow({ menuId, dateStr, slotType, displayOrder, options, slot, onUpsert, onDelete, isPending }: SlotRowProps) {
  const product = slot?.product;
  const productId = slot?.productId ?? null;
  const [portions, setPortions] = useState<number | null>(() => {
    const n = slot?.estimatedPortions;
    return n != null && n > 0 ? n : null;
  });
  const estimatedPortionsForSave =
    portions != null && portions > 0 ? portions : null;
  const [productInput, setProductInput] = useState<Product | null>((product ?? null) as Product | null);
  const showDelete = (slotType === "PLATS" || slotType === "DESSERT") && slot?.id && onDelete;

  const selectedProductId = productInput?.id ?? productId;

  // Pour les plats : tri par catégorie pour un regroupement cohérent dans l’Autocomplete
  const sortedOptions = useMemo(() => {
    if (slotType !== "PLATS") return options;
    const order: (DishCategory | null)[] = ["FISH", "GRILLED_MEAT", "VEGETARIAN", "TRADITIONAL", "EXOTIC", "STEW", "OTHER", null];
    return [...options].sort((a, b) => {
      const ia = order.indexOf(a.dishCategory ?? null);
      const ib = order.indexOf(b.dishCategory ?? null);
      if (ia !== ib) return ia - ib;
      return (a.name ?? "").localeCompare(b.name ?? "");
    });
  }, [options, slotType]);

  const handleProductChange = (_: unknown, value: Product | { id: string; name: string; price?: string | number } | null) => {
    setProductInput(value != null && "id" in value ? (value as Product) : null);
    const switchVal = slot?.isSwitch ?? false;
    if (value !== null && value !== undefined && "id" in value) {
      onUpsert(menuId, dateStr, slotType, displayOrder, slot?.id, value.id, estimatedPortionsForSave, switchVal);
    } else if (slot?.id) {
      onUpsert(menuId, dateStr, slotType, displayOrder, slot.id, null, estimatedPortionsForSave, false);
    }
  };

  const handlePortionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") {
      setPortions(null);
      return;
    }
    const v = parseInt(raw, 10);
    setPortions(Number.isNaN(v) || v <= 0 ? null : v);
  };

  const handlePortionsBlur = () => {
    onUpsert(
      menuId,
      dateStr,
      slotType,
      displayOrder,
      slot?.id,
      selectedProductId ?? null,
      estimatedPortionsForSave,
      slot?.isSwitch ?? false
    );
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    onUpsert(
      menuId,
      dateStr,
      slotType,
      displayOrder,
      slot?.id,
      selectedProductId ?? null,
      estimatedPortionsForSave,
      checked
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
      <Autocomplete
        size="small"
        options={sortedOptions}
        getOptionLabel={(p) => p.name}
        groupBy={slotType === "PLATS" ? (p: Product) => DISH_CATEGORY_LABELS[(p.dishCategory ?? "OTHER") as DishCategory] ?? "Autre" : undefined}
        isOptionEqualToValue={(a, b) => (a?.id && b?.id ? a.id === b.id : a === b)}
        value={productInput}
        onChange={handleProductChange}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={slotType === "PLATS" ? "Rechercher un plat..." : "Rechercher..."}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {slotType === "ENTREE" && <Search sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />}
                  {slotType === "LEGUMES_100" && <LocalFlorist sx={{ color: "success.main", mr: 1, fontSize: 20 }} />}
                  {slotType === "DESSERT" && <Cake sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />}
                  {slotType === "PREMIUM" && <Star sx={{ color: "warning.main", mr: 1, fontSize: 20 }} />}
                  {slotType === "PREMIUM_PLUS" && <Diamond sx={{ color: "warning.main", mr: 1, fontSize: 20 }} />}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        disabled={isPending}
      />
      {productInput ? (
        <Box
          sx={{
            p: 1.5,
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                flexShrink: 0,
                borderRadius: "8px",
                bgcolor: "grey.100",
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...(productInput.imageUrl && { backgroundImage: `url(${productInput.imageUrl})` }),
              }}
            >
              {!productInput.imageUrl && <Restaurant sx={{ color: "grey.400", fontSize: 32 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
                <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "secondary.main", fontSize: "14px", whiteSpace: "nowrap" }}>
                  {Number(productInput.price ?? 0).toFixed(2)} €
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  mt: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                }}
              >
                {productInput.description || "Aucune description"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                {productInput.category && (
                  <Box component="span" sx={{ fontSize: "10px", px: 0.75, py: 0.25, bgcolor: "#dbeafe", color: "#1d4ed8", borderRadius: "4px", border: "1px solid #bfdbfe" }}>
                    {productInput.category.name}
                  </Box>
                )}
                {productInput.isVegetarian && (
                  <Box component="span" sx={{ fontSize: "10px", px: 0.75, py: 0.25, bgcolor: "#dcfce7", color: "#15803d", borderRadius: "4px", border: "1px solid #bbf7d0" }}>
                    Végétarien
                  </Box>
                )}
                {productInput.isRecurrent && (
                  <Box component="span" sx={{ fontSize: "10px", px: 0.75, py: 0.25, bgcolor: "#f3e8ff", color: "#7c3aed", borderRadius: "4px", border: "1px solid #e9d5ff" }}>
                    Récurrent
                  </Box>
                )}
                {slot?.isSwitch && (
                  <Box component="span" sx={{ fontSize: "10px", px: 0.75, py: 0.25, bgcolor: "#fef3c7", color: "#b45309", borderRadius: "4px", border: "1px solid #fde68a" }}>
                    Switch
                  </Box>
                )}
                {productInput.isPremium && (
                  <Box component="span" sx={{ fontSize: "10px", px: 0.75, py: 0.25, bgcolor: "#fef9c3", color: "#a16207", borderRadius: "4px", border: "1px solid #fef08a" }}>
                    Premium
                  </Box>
                )}
                {productInput.isSpicy && (
                  <Box component="span" sx={{ fontSize: "10px", px: 0.75, py: 0.25, bgcolor: "#fee2e2", color: "#b91c1c", borderRadius: "4px", border: "1px solid #fecaca" }}>
                    Épicé
                  </Box>
                )}
              </Box>
            </Box>
            {showDelete && (
              <IconButton size="small" onClick={onDelete} aria-label="Supprimer le plat" color="error" disabled={isPending}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {slotType === "PLATS" && productInput && (
            <Box sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: "divider", pt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={slot?.isSwitch ?? false}
                    onChange={handleSwitchChange}
                    disabled={isPending}
                  />
                }
                label="Option Switch"
              />
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", borderTop: 1, borderColor: "divider", pt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Est. portions
            </Typography>
            <TextField
              type="number"
              size="small"
              value={portions != null && portions > 0 ? portions : ""}
              onChange={handlePortionsChange}
              onBlur={handlePortionsBlur}
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 64, "& .MuiInputBase-input": { textAlign: "center", py: 0.5 } }}
              disabled={isPending}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}